import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Inject, Injectable, Optional, resource, Resource } from '@angular/core';
import { firstValueFrom, fromEvent, Observable, race } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiErrorService } from './error';

export class AbortError extends Error {
  override name = 'AbortError';
  constructor() {
    super('Request aborted');
  }
}

export class HttpStatusError extends Error {
  override name = 'HttpStatusError';
  /**
   * Machine-readable error code from the response body (`err.error.code`), when present.
   * Lets callers branch on a specific backend error (e.g. `CALENDAR_SLOT_BLOCKED`) instead
   * of a bare status, so a 409 for an unrelated conflict isn't misclassified.
   *
   * `data` carries the response body's structured `data` payload (`err.error.data`) — e.g. the
   * per-record `{ failed: [...] }` breakdown from a MAR bulk-record 422 — which callers otherwise
   * lose once the raw `HttpErrorResponse` is wrapped here.
   */
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly data?: Record<string, unknown>
  ) {
    super(message);
  }
}

/**
 * Returns true when BaseApiService.handleError already showed an error toast for this status.
 * 401, 403, and network errors (status 0) are propagated silently — callers must show their own notification.
 * Use this to avoid double-toasting after an HttpStatusError is caught upstream.
 */
export function wasToastedByApiService(status: number): boolean {
  return status !== 401 && status !== 403 && status !== 0 && status >= 400;
}

/**
 * The HTTP status of an error, whether it arrived as a raw `HttpErrorResponse` (callers using raw
 * `http.post`, e.g. `MedicationsApiService`) or a `BaseApiService`-wrapped `HttpStatusError`.
 * Returns `undefined` for non-HTTP errors. Keeps the dual-type guard in one place.
 */
export function getHttpStatus(err: unknown): number | undefined {
  return err instanceof HttpStatusError || err instanceof HttpErrorResponse ? err.status : undefined;
}

/**
 * True when an error is a 409 Conflict, whether it arrived as a raw `HttpErrorResponse` or a
 * `BaseApiService`-wrapped `HttpStatusError`. Lets callers detect the conflict without coupling to
 * which layer their API happens to throw from.
 */
export function is409Conflict(err: unknown): boolean {
  return getHttpStatus(err) === 409;
}

/**
 * True when an error is a 403 Forbidden, whether it arrived as a raw `HttpErrorResponse` or a
 * `BaseApiService`-wrapped `HttpStatusError`.
 */
export function isForbidden(err: unknown): boolean {
  return getHttpStatus(err) === 403;
}

/** The JSON error payload the API answers with. All fields are optional — bodies vary by endpoint. */
export interface ApiErrorBody {
  message?: string;
  code?: string;
  data?: Record<string, unknown>;
}

/**
 * The error body as an object, parsing it first when the response arrived as a raw string.
 * Requests made with `responseType: 'text'` — needed by endpoints whose *success* body is plain
 * text (`res.sendStatus(200)` sends `OK`, which is not JSON) — hand us the unparsed response, so
 * `message` and `code` would silently read as `undefined` on failure. Returns `undefined` for
 * bodies that are not JSON objects (a bare `"Unauthorized"` string included), which is what keeps
 * those falling through to the synthesized message.
 */
export function parseErrorBody(body: unknown): ApiErrorBody | undefined {
  let parsed = body;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return undefined;
    }
  }
  return typeof parsed === 'object' && parsed !== null ? (parsed as ApiErrorBody) : undefined;
}

export const CONNECTION_ERROR_MESSAGE = 'Unable to connect to server';
export const UNKNOWN_ERROR_MESSAGE = 'An unexpected error occurred';

/**
 * The message `handleError` falls back to when a response body carries no `message`.
 * `handleError` builds its placeholders through this function and `isSynthesizedHttpMessage`
 * recognizes them, so the two cannot drift apart.
 */
function synthesizeHttpMessage(status: number): string {
  if (!status) return CONNECTION_ERROR_MESSAGE;
  return status >= 500 ? `Server error: ${status}` : `Client error: ${status}`;
}

/**
 * True when `handleError` synthesized this message because the response body carried no `message`.
 * Callers that surface the server's message to the user must check this first — a synthesized value
 * is internal technical text, so they should fall back to their own translated string instead.
 */
export function isSynthesizedHttpMessage(message: string, status: number): boolean {
  const msg = message?.trim();
  if (!msg) return true;
  return (
    msg === synthesizeHttpMessage(status) ||
    msg === `Error ${status}` || // the 401/403 fallback below
    msg === CONNECTION_ERROR_MESSAGE ||
    msg === UNKNOWN_ERROR_MESSAGE
  );
}

/**
 * The server's own error message when it sent a real one, else the caller's translated `fallback`.
 *
 * The standard shape for any caller that passes `suppressToast` and is therefore the only error
 * surface: `handleError` synthesizes "Client error: 404" / "Server error: 500" when the response
 * body carries no `message`, and showing that internal text to a user is worse than a generic
 * translated string.
 */
export function apiErrorMessageOr(err: unknown, fallback: string): string {
  if (!(err instanceof HttpStatusError)) return fallback;
  const message = err.message?.trim();
  if (!message || isSynthesizedHttpMessage(message, err.status)) return fallback;
  return message;
}

export interface ApiRequestOptions {
  headers?: Record<string, string>;
  /** Optional request body for DELETE endpoints that record mutation context. */
  body?: unknown;
  suppressToast?: boolean;
  /** When true, 404/410 responses throw a typed error (e.g. EChartItemNotFoundError) instead of a generic Error */
  throwTypedNotFound?: boolean;
  responseType?: 'json' | 'text';
  signal?: AbortSignal;
}

/**
 * Base API service providing common HTTP operations using Angular Resources
 * This service can be extended by feature services for specific API endpoints
 *
 * Example usage:
 * ```typescript
 * @Injectable({ providedIn: 'root' })
 * export class UserService extends BaseApiService<User> {
 *   constructor() {
 *     super('/api/users');
 *   }
 *
 *   // Create a resource for fetching users
 *   private usersResource = this.createGetResource<User[]>();
 *
 *   // Expose resource signals
 *   users = this.usersResource.value;
 *   loading = this.usersResource.isLoading;
 *   error = this.usersResource.error;
 *
 *   reloadUsers() {
 *     this.usersResource.reload();
 *   }
 *
 *   // Resource with reactive parameter
 *   private searchQuery = signal('');
 *   private searchResource = this.createGetResource<User[]>(
 *     '/search',
 *     () => this.searchQuery()
 *   );
 *
 *   searchResults = this.searchResource.value;
 *   searchLoading = this.searchResource.isLoading;
 * }
 * ```
 */
@Injectable()
export class BaseApiService<T> {
  protected readonly http: HttpClient;
  protected readonly errorService = inject(ApiErrorService);
  protected readonly baseUrlOrPromise: string | Promise<string>;

  constructor(@Optional() @Inject('API_BASE_URL') baseUrl?: string | Promise<string>) {
    this.http = inject(HttpClient);
    this.baseUrlOrPromise = baseUrl || '';
  }

  /**
   * Resolves the base URL, supporting both sync and async values
   */
  protected async getBaseUrl(): Promise<string> {
    return this.baseUrlOrPromise instanceof Promise ? await this.baseUrlOrPromise : this.baseUrlOrPromise;
  }

  /**
   * Create a resource for GET request
   * @param endpoint API endpoint (relative to baseUrl)
   * @param request Optional reactive request signal/function that triggers reload when changed
   */
  public createGetResource<R = T>(endpoint: string = '', request?: () => unknown): Resource<R> {
    return resource({
      request,
      loader: async () => {
        const baseUrl = await this.getBaseUrl();
        return await firstValueFrom(
          this.http.get<R>(`${baseUrl}${endpoint}`).pipe(
            catchError((err: HttpErrorResponse) => {
              throw this.handleError(err);
            })
          )
        );
      },
    });
  }

  /**
   * Create a resource for GET request with dynamic endpoint
   * @param endpointFn Function that returns the endpoint (called on each request)
   * @param request Optional reactive request signal/function that triggers reload when changed
   * @param options Optional request options (e.g. `suppressToast` to let the caller render its own error state)
   */
  public createDynamicGetResource<R = T>(
    endpointFn: () => string | null,
    request?: () => unknown,
    options?: ApiRequestOptions
  ): Resource<R> {
    return resource({
      request,
      loader: async () => {
        const endpoint = endpointFn();
        if (endpoint === null) {
          return [] as unknown as R;
        }
        const baseUrl = await this.getBaseUrl();
        return await firstValueFrom(
          this.http.get<R>(`${baseUrl}${endpoint}`).pipe(
            catchError((err: HttpErrorResponse) => {
              throw this.handleError(err, options);
            })
          )
        );
      },
    });
  }

  /**
   * Perform a POST request (async, non-reactive)
   * @param endpoint API endpoint (relative to baseUrl)
   * @param body Request body
   */
  public async post<R = T>(endpoint: string = '', body: unknown = {}, options?: ApiRequestOptions): Promise<R> {
    const baseUrl = await this.getBaseUrl();
    return await this.resolve(
      this.http.post<R>(`${baseUrl}${endpoint}`, body, this.buildRequestOptions(options)).pipe(
        catchError((err: HttpErrorResponse) => {
          throw this.handleError(err, options);
        })
      ),
      options?.signal
    );
  }

  /**
   * Perform a GET request (async, non-reactive)
   * @param endpoint API endpoint (relative to baseUrl)
   * @param options Optional query params
   */
  public async get<R = T>(
    endpoint: string = '',
    options?: { params?: Record<string, string> } & ApiRequestOptions
  ): Promise<R> {
    const baseUrl = await this.getBaseUrl();
    return await this.resolve(
      this.http.get<R>(`${baseUrl}${endpoint}`, this.buildRequestOptions(options)).pipe(
        catchError((err: HttpErrorResponse) => {
          throw this.handleError(err, options);
        })
      ),
      options?.signal
    );
  }

  /**
   * Perform a PUT request (async, non-reactive)
   * @param endpoint API endpoint (relative to baseUrl)
   * @param body Request body
   */
  public async put<R = T>(endpoint: string = '', body: unknown = {}, options?: ApiRequestOptions): Promise<R> {
    const baseUrl = await this.getBaseUrl();
    return await this.resolve(
      this.http.put<R>(`${baseUrl}${endpoint}`, body, this.buildRequestOptions(options)).pipe(
        catchError((err: HttpErrorResponse) => {
          throw this.handleError(err, options);
        })
      ),
      options?.signal
    );
  }

  /**
   * Perform a DELETE request (async, non-reactive)
   * @param endpoint API endpoint (relative to baseUrl)
   */
  public async delete<R = void>(endpoint: string = '', options?: ApiRequestOptions): Promise<R> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}${endpoint}`;
    const requestOptions = {
      ...this.buildRequestOptions(options),
      ...(options?.body !== undefined ? { body: options.body } : {}),
    };
    const handleErr = (err: HttpErrorResponse): never => {
      throw this.handleError(err, options);
    };
    if (options?.responseType === 'text') {
      return (await this.resolve(
        this.http
          .delete(url, {
            ...requestOptions,
            responseType: 'text',
          })
          .pipe(catchError(handleErr)),
        options?.signal
      )) as R;
    }
    return await this.resolve(this.http.delete<R>(url, requestOptions).pipe(catchError(handleErr)), options?.signal);
  }

  /**
   * Perform a PATCH request (async, non-reactive)
   * @param endpoint API endpoint (relative to baseUrl)
   * @param body Request body
   */
  public async patch<R = T>(endpoint: string = '', body: unknown = {}, options?: ApiRequestOptions): Promise<R> {
    const baseUrl = await this.getBaseUrl();
    return await this.resolve(
      this.http.patch<R>(`${baseUrl}${endpoint}`, body, this.buildRequestOptions(options)).pipe(
        catchError((err: HttpErrorResponse) => {
          throw this.handleError(err, options);
        })
      ),
      options?.signal
    );
  }

  private resolve<R>(obs: Observable<R>, signal?: AbortSignal): Promise<R> {
    if (!signal) return firstValueFrom(obs);
    if (signal.aborted) return Promise.reject(new AbortError());
    return firstValueFrom(
      race(
        obs,
        fromEvent(signal, 'abort').pipe(
          map(() => {
            throw new AbortError();
          })
        )
      )
    );
  }

  private buildRequestOptions(options?: { params?: Record<string, string> } & ApiRequestOptions):
    | {
        headers?: Record<string, string>;
        params?: Record<string, string>;
      }
    | undefined {
    if (!options?.params && !options?.headers) return undefined;
    return {
      ...(options.params ? { params: options.params } : {}),
      ...(options.headers ? { headers: options.headers } : {}),
    };
  }

  /**
   * Create a resource for POST request
   * @param endpoint API endpoint (relative to baseUrl)
   * @param body Request body or function that returns the body
   * @param request Optional reactive request signal/function that triggers reload when changed
   */
  public createPostResource<R = T>(
    endpoint: string = '',
    body: unknown | (() => unknown) = {},
    request?: () => unknown
  ): Resource<R> {
    return resource({
      request,
      loader: async () => {
        const baseUrl = await this.getBaseUrl();
        const requestBody = typeof body === 'function' ? body() : body;
        if (requestBody === null) return undefined as unknown as R;
        return await firstValueFrom(
          this.http.post<R>(`${baseUrl}${endpoint}`, requestBody).pipe(
            catchError((err: HttpErrorResponse) => {
              throw this.handleError(err);
            })
          )
        );
      },
    });
  }

  /**
   * Create a resource for PUT request
   * @param endpoint API endpoint (relative to baseUrl)
   * @param body Request body or function that returns the body
   * @param request Optional reactive request signal/function that triggers reload when changed
   */
  public createPutResource<R = T>(
    endpoint: string = '',
    body: unknown | (() => unknown) = {},
    request?: () => unknown
  ): Resource<R> {
    return resource({
      request,
      loader: async () => {
        const baseUrl = await this.getBaseUrl();
        const requestBody = typeof body === 'function' ? body() : body;
        return await firstValueFrom(
          this.http.put<R>(`${baseUrl}${endpoint}`, requestBody).pipe(
            catchError((err: HttpErrorResponse) => {
              throw this.handleError(err);
            })
          )
        );
      },
    });
  }

  /**
   * Create a resource for PATCH request
   * @param endpoint API endpoint (relative to baseUrl)
   * @param body Request body or function that returns the body
   * @param request Optional reactive request signal/function that triggers reload when changed
   */
  public createPatchResource<R = T>(
    endpoint: string = '',
    body: unknown | (() => unknown) = {},
    request?: () => unknown
  ): Resource<R> {
    return resource({
      request,
      loader: async () => {
        const baseUrl = await this.getBaseUrl();
        const requestBody = typeof body === 'function' ? body() : body;
        return await firstValueFrom(
          this.http.patch<R>(`${baseUrl}${endpoint}`, requestBody).pipe(
            catchError((err: HttpErrorResponse) => {
              throw this.handleError(err);
            })
          )
        );
      },
    });
  }

  /**
   * Create a resource for DELETE request
   * @param endpoint API endpoint (relative to baseUrl)
   * @param request Optional reactive request signal/function that triggers reload when changed
   */
  public createDeleteResource<R = void>(endpoint: string = '', request?: () => unknown): Resource<R> {
    return resource({
      request,
      loader: async () => {
        const baseUrl = await this.getBaseUrl();
        return await firstValueFrom(
          this.http.delete<R>(`${baseUrl}${endpoint}`).pipe(
            catchError((err: HttpErrorResponse) => {
              throw this.handleError(err);
            })
          )
        );
      },
    });
  }

  /**
   * Handle HTTP errors and return Error object
   * Uses ApiErrorService for application-level error handling (400-499, 500+)
   * 401, 403, and network errors skip toast handling and propagate to callers as HttpStatusError
   */
  protected handleError(err: HttpErrorResponse, options?: ApiRequestOptions): Error {
    const errorBody = parseErrorBody(err.error);

    // 401, 403, and network errors: skip toast, propagate status to callers via HttpStatusError
    if (err.status === 401 || err.status === 403 || !err.status) {
      const errorMessage = err.status === 0 ? CONNECTION_ERROR_MESSAGE : errorBody?.message || `Error ${err.status}`;
      const code = typeof errorBody?.code === 'string' ? errorBody.code : undefined;
      return new HttpStatusError(errorMessage, err.status, code);
    }

    // Handle application-level errors (400-499 client errors, 500+ server errors)
    if (!options?.suppressToast) {
      if (err.status >= 400 && err.status < 500) {
        // Client errors - show specific error message
        this.errorService.handleApiError(err);
      } else if (err.status >= 500) {
        // Server errors - show user-friendly message
        this.errorService.handleApiError(err);
      }
    }

    // Extract error message for throwing
    let errorMessage = UNKNOWN_ERROR_MESSAGE;

    if (err.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = err.error.message;
    } else {
      // Server-side error
      const raw = errorBody?.message;
      const safeMessage = typeof raw === 'string' ? raw : null;
      if (err.status === 0) {
        errorMessage = CONNECTION_ERROR_MESSAGE;
      } else if (err.status >= 400) {
        errorMessage = safeMessage || synthesizeHttpMessage(err.status);
      }
    }

    const code = typeof errorBody?.code === 'string' ? errorBody.code : undefined;
    const data =
      typeof errorBody?.data === 'object' && errorBody?.data !== null
        ? (errorBody.data as Record<string, unknown>)
        : undefined;
    return new HttpStatusError(errorMessage, err.status, code, data);
  }
}
